/**
 * Resolve a URL-provided player name against the loaded index + alias map.
 *
 * Returns { canonical, record, redirected } where:
 *   - canonical: the display-cased name from the index
 *   - record: the player record (or null if not found)
 *   - redirected: true if the input was an alias or casing mismatch
 *
 * @param {string} raw       - the ?player= value (URL-decoded)
 * @param {Object} index     - the full playerIndex keyed by display name
 * @param {Object} aliases   - nameMigrations.alias: { canonKey oldName: canonKey newName }
 */
export function resolvePlayerName(raw, index, aliases) {
  if (!raw) return { canonical: null, record: null, redirected: false };

  // 1. Check alias map (keyed in canonKey form: lowercase, underscore-collapsed)
  const aliasTarget = aliases && aliases[collapse(raw)];
  if (aliasTarget) {
    // The alias points to the canonical key — find it in the index
    const entry = findInIndex(aliasTarget, index);
    if (entry) return { canonical: entry[0], record: entry[1], redirected: true };
  }

  // 2. Exact match in the index
  if (index[raw]) return { canonical: raw, record: index[raw], redirected: false };

  // 3. Collapsed match in the index (case + space/underscore variants)
  const entry = findInIndex(raw, index);
  if (entry) return { canonical: entry[0], record: entry[1], redirected: entry[0] !== raw };

  return { canonical: raw, record: null, redirected: false };
}

// Same collapse rule as tools/nameKey.mjs canonKey: Twitch is case-insensitive and
// swaps underscores for spaces in display names (not always 1:1 — trailing/doubled
// underscores survive in some feeds), so every run of whitespace/underscore becomes
// one underscore and leading/trailing separators are stripped. Index keys are spaced
// display names while alias keys and real handles use underscores — collapsing both
// sides makes "Bkg Kit", "bkg_kit", and "bkg__kit_" the same key.
function collapse(name) {
  return (name || '').toLowerCase().replace(/[\s_]+/g, '_').replace(/^_+|_+$/g, '');
}

/** Collapsed-key lookup in the index. Returns [displayName, record] or null. */
function findInIndex(name, index) {
  const key = collapse(name);
  for (const k in index) {
    if (collapse(k) === key) return [k, index[k]];
  }
  return null;
}
